import React, { useEffect, useState, useCallback } from "react";
import { Input, Avatar, Col, message, Spin, Button, Typography } from "antd";
import { CommentOutlined, SendOutlined, EditOutlined } from "@ant-design/icons";
import useAuth from "../../../hooks/useAuth";
import instance from "../../../api/cmsapi";

const { Text, Paragraph } = Typography;

const REVIEWER_COMMENT_ROLES = new Set(["APR_RCC", "RCC_USER", "APR_DCE", "APRDCE"]);
const isReviewerCommentRole = (role) => {
  const normalized = String(role || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  return REVIEWER_COMMENT_ROLES.has(String(role || "").toUpperCase()) || REVIEWER_COMMENT_ROLES.has(normalized);
};

function APRComment({
  data,
  year,
  districtId,
  tableCommentedId,
  children,
  assessmentStatus,
  hideTableDis = false,
}) {
  const { user } = useAuth();
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [comments, setComments] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editText, setEditText] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentUserRole =
    user?.user?.userRoles?.find((role) => role.name === "APR DCE")?.name || "";
  const normalizedUserRole = currentUserRole ? currentUserRole.replace(/ /g, "_").toUpperCase() : "";
  const storedCommentRole = "APR_DCE";
  const currentUsername = user?.user?.username || "";
  const currentFullName = user?.user?.fullName || "";

  const fetchComments = useCallback(async () => {
    if (!districtId || !year) return;
    setLoading(true);
    try {
      const response = await instance.get(`comments/tables/${districtId}/${year}/APR`);
      const filteredComments = response.data.filter(
        (comment) =>
          comment.tableCommented === tableCommentedId &&
          comment.districtId === districtId &&
          (isReviewerCommentRole(comment.userRole) || comment.userRole === "APR_USER")
      );
      setComments(filteredComments);
      setError(null);
    } catch (error) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [districtId, year, tableCommentedId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleCommentSubmit = async (submitText = commentText) => {
    if (!submitText.trim()) {
      message.error("Comment cannot be empty");
      return;
    }
    if (!currentUsername || !districtId || !year) {
      message.error("User or district information is missing");
      return;
    }

    const existingComment = comments.find(
      (comment) =>
        comment.tableCommented === tableCommentedId &&
        comment.districtId === districtId &&
        isReviewerCommentRole(comment.userRole) &&
        comment.username === currentUsername
    );

    const hasOtherComment = comments.some(
      (comment) =>
        comment.tableCommented === tableCommentedId &&
        comment.districtId === districtId &&
        isReviewerCommentRole(comment.userRole) &&
        comment.username !== currentUsername
    );

    if (editingCommentId) {
      const commentToEdit = comments.find((c) => c.id === editingCommentId);
      if (!commentToEdit || commentToEdit.username !== currentUsername || !isReviewerCommentRole(commentToEdit.userRole)) {
        message.error("You can only edit your own comment.");
        return;
      }
      const commentDate = new Date().toISOString().split("T")[0].split("-").map(Number);
      const payload = {
        id: commentToEdit.id,
        username: currentUsername,
        fullName: currentFullName,
        userRole: storedCommentRole,
        type: "APR",
        districtId,
        year,
        tableCommented: tableCommentedId,
        comments: submitText,
        gaps: commentToEdit.gaps || "",
        commentDate: commentToEdit.commentDate || commentDate,
        updateDate: commentDate,
        dddpDataDate: commentDate,
        dddpData: { indicator: tableCommentedId, tables: "" },
      };

      setLoading(true);
      try {
        await instance.put(`comments/${commentToEdit.id}`, payload);
        setComments((prevComments) =>
          prevComments.map((comment) =>
            comment.id === commentToEdit.id
              ? { ...comment, comments: submitText, updateDate: commentDate }
              : comment
          )
        );
        message.success("Comment updated successfully");
        setEditingCommentId(null);
        setEditText({});
        setCommentText("");
        setShowCommentInput(false);
        await fetchComments();
      } catch (error) {
        await fetchComments();
        setError("Failed to save comment");
        message.error(`Failed to save comment: ${error.response?.data?.message || error.message}`);
      } finally {
        setLoading(false);
      }
    } else if (!existingComment && !hasOtherComment) {
      const commentDate = new Date().toISOString().split("T")[0].split("-").map(Number);
      const payload = {
        id: 0,
        username: currentUsername,
        fullName: currentFullName,
        userRole: storedCommentRole,
        type: "APR",
        districtId,
        year,
        tableCommented: tableCommentedId,
        comments: submitText,
        gaps: "",
        commentDate: commentDate,
        updateDate: commentDate,
        dddpDataDate: commentDate,
        dddpData: { indicator: tableCommentedId, tables: "" },
      };

      setLoading(true);
      try {
        const tempId = Date.now();
        setComments((prevComments) => [...prevComments, { ...payload, id: tempId }]);
        const response = await instance.post("comments", payload);
        setComments((prevComments) =>
          prevComments.map((comment) => (comment.id === tempId ? { ...comment, id: response.data.id } : comment))
        );
        message.success("Comment added successfully");
        setCommentText("");
        setShowCommentInput(false);
        await fetchComments();
      } catch (error) {
        await fetchComments();
        setError("Failed to save comment");
        message.error(`Failed to save comment: ${error.response?.data?.message || error.message}`);
      } finally {
        setLoading(false);
      }
    } else if (existingComment) {
      message.error("You can only submit one comment per table.");
    } else if (hasOtherComment) {
      message.error("Another user has already added a comment to this table.");
    }
  };

  const handleEditComment = (comment) => {
    if (comment.username !== currentUsername || !isReviewerCommentRole(comment.userRole)) {
      message.error("You can only edit your own comment.");
      return;
    }
    setEditingCommentId(comment.id);
    setEditText({ ...editText, [comment.id]: comment.comments });
  };

  const canShowCommentInput = () => {
    const existingCommentByUser = comments.find(
      (comment) =>
        comment.tableCommented === tableCommentedId &&
        comment.districtId === districtId &&
        isReviewerCommentRole(comment.userRole) &&
        comment.username === currentUsername
    );
    const hasOtherComment = comments.some(
      (comment) =>
        comment.tableCommented === tableCommentedId &&
        comment.districtId === districtId &&
        isReviewerCommentRole(comment.userRole) &&
        comment.username !== currentUsername
    );
    return !existingCommentByUser && !hasOtherComment;
  };

  const toggleCommentComposer = () => {
    if (showCommentInput) {
      setShowCommentInput(false);
      setCommentText("");
      return;
    }
    if (canShowCommentInput()) setShowCommentInput(true);
    else message.error("Only one DCE comment is allowed per table.");
  };

  const renderCommentInput = () => {
    if (!normalizedUserRole || hideTableDis || assessmentStatus === false) return null;

    return (
      <div style={{ marginTop: 18, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
        <div
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            background: "#fff",
            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              padding: "14px 16px",
              background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 65%)",
              borderBottom: showCommentInput ? "1px solid #f1f5f9" : "none",
            }}
          >
            <div style={{ flex: "1 1 200px", minWidth: 0 }}>
              <Text strong style={{ display: "block", fontSize: 13, color: "#0f172a" }}>
                APR DCE review note
              </Text>
              <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
                Add a concise comment for this table. RCC users see these when the report is sent back.
              </Text>
            </div>
            <Button
              type={showCommentInput ? "default" : "primary"}
              icon={<CommentOutlined />}
              onClick={toggleCommentComposer}
              size="middle"
              style={{ borderRadius: 8, flexShrink: 0, fontWeight: 500 }}
            >
              {showCommentInput ? "Close" : "Add comment"}
            </Button>
          </div>
          {showCommentInput && (
            <div style={{ padding: "16px 16px 14px" }}>
              <Spin spinning={loading}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                  <Avatar
                    src={
                      user?.image ||
                      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTL_JlCFnIGX5omgjEjgV9F3sBRq14eTERK9w&s"
                    }
                    size={38}
                    style={{ flexShrink: 0, border: "2px solid #e2e8f0" }}
                  />
                  <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                    <Input.TextArea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          if (commentText.trim()) handleCommentSubmit();
                        }
                      }}
                      placeholder="Write your comment… (Shift + Enter for a new line)"
                      autoSize={{ minRows: 3, maxRows: 8 }}
                      style={{
                        borderRadius: 8,
                        fontSize: 14,
                        fontFamily: '"Source Sans 3", "Segoe UI", system-ui, sans-serif',
                        lineHeight: 1.55,
                        padding: "10px 12px",
                        resize: "vertical",
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                        gap: 8,
                        marginTop: 12,
                      }}
                    >
                      <Button size="middle" style={{ borderRadius: 8 }} onClick={() => toggleCommentComposer()}>
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        loading={loading}
                        disabled={!commentText.trim()}
                        onClick={() => handleCommentSubmit()}
                        style={{ borderRadius: 8 }}
                      >
                        Post comment
                      </Button>
                    </div>
                  </div>
                </div>
              </Spin>
              {error && (
                <Text type="danger" style={{ display: "block", marginTop: 10, fontSize: 13 }}>
                  {error}
                </Text>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCommentList = () => (
    <Spin spinning={loading}>
      <div
        style={{
          marginTop: 14,
          width: "100%",
          maxWidth: "100%",
          padding: comments.length ? "4px 0 0" : 0,
        }}
      >
        {comments.filter((c) => c.comments && c.comments.trim() !== "").length > 0 && (
          <Text type="secondary" style={{ fontSize: 12, letterSpacing: 0.02, textTransform: "uppercase" }}>
            Thread
          </Text>
        )}
        {comments.filter((comment) => comment.comments && comment.comments.trim() !== "").length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              marginTop: 10,
            }}
          >
            {comments
              .filter((comment) => comment.comments && comment.comments.trim() !== "")
              .map((comment) => (
                <div
                  key={comment.id}
                  style={{
                    padding: "14px 16px",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    background: "#fff",
                    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                  }}
                >
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <Avatar
                      src={
                        comment.userImage ||
                        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTL_JlCFnIGX5omgjEjgV9F3sBRq14eTERK9w&s"
                      }
                      size={36}
                      style={{ flexShrink: 0 }}
                    />
                    <Col style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "baseline",
                          gap: "6px 10px",
                          marginBottom: 6,
                        }}
                      >
                        <Text strong style={{ fontSize: 13, color: "#0f172a" }}>
                          {comment.fullName}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {(comment.userRole || "").replace(/_/g, " ")}
                        </Text>
                        {comment.commentDate?.length ? (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            · {comment.commentDate.join("/")}
                          </Text>
                        ) : null}
                        {comment.username === currentUsername && isReviewerCommentRole(comment.userRole) ? (
                          <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEditComment(comment)}
                            style={{ padding: 0, height: "auto", fontSize: 12 }}
                          >
                            Edit
                          </Button>
                        ) : null}
                      </div>
                      {isReviewerCommentRole(comment.userRole) && editingCommentId === comment.id ? (
                        <div style={{ marginTop: 4 }}>
                          <Input.TextArea
                            value={editText[comment.id] || ""}
                            onChange={(e) =>
                              setEditText({ ...editText, [comment.id]: e.target.value })
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleCommentSubmit(editText[comment.id]);
                              }
                            }}
                            autoSize={{ minRows: 3, maxRows: 8 }}
                            style={{
                              borderRadius: 8,
                              fontSize: 14,
                              fontFamily: '"Source Sans 3", "Segoe UI", system-ui, sans-serif',
                              lineHeight: 1.55,
                            }}
                          />
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: 8,
                              marginTop: 10,
                              flexWrap: "wrap",
                            }}
                          >
                            <Button
                              size="small"
                              onClick={() => {
                                setEditingCommentId(null);
                                setEditText((t) => {
                                  const n = { ...t };
                                  delete n[comment.id];
                                  return n;
                                });
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="primary"
                              size="small"
                              icon={<SendOutlined />}
                              loading={loading}
                              disabled={!editText[comment.id]?.trim()}
                              onClick={() => handleCommentSubmit(editText[comment.id])}
                              style={{ borderRadius: 8 }}
                            >
                              Save
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Paragraph
                          style={{
                            marginBottom: 0,
                            fontSize: 14,
                            lineHeight: 1.55,
                            color: "#334155",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            fontFamily: '"Source Sans 3", "Segoe UI", system-ui, sans-serif',
                          }}
                        >
                          {comment.comments}
                        </Paragraph>
                      )}
                    </Col>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </Spin>
  );

  return children({ renderCommentInput, renderCommentList });
}

export default APRComment;
